import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { DyslipidemiaInputOption } from '@app/models/input-option';
import { SelectionsModel, ControlTypeEnum } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-dyslipidemia',
  templateUrl: './dyslipidemia.component.html',
  styleUrls: ['./dyslipidemia.component.css']
})
export class DyslipidemiaComponent implements OnInit {

  @ViewChild('childDiagnosisComponent') childDiagnosisComponent: RadioInputGroupComponent;

  @Input() indication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  numberOfSelectionsListItems: number[] = [0,1,2,3,4];
  numberOfSelectionsValue: number = 1;
  hasErrors = false;

  childDiagnosisSelectionModel: SelectionsModel;

  dyslipidemiaCriteriaOptions: DyslipidemiaInputOption[] = [
    {
      checkbox: {
        name: 'HDL_Checkbox',
        displayName: 'HDL Upper Limit',
        defaultValue: true,
        order: 1,
        details: '(<)'
      },
      male: {
        name: 'HDL_Male',
        displayName: 'Male',
        defaultValue: '40',
        order: 1
      },
      female: {
        name: 'HDL_Female',
        displayName: 'Female',
        defaultValue: '50',
        order: 2
      }
    },
    {
      checkbox: {
        name: 'LDL_Checkbox',
        displayName: 'LDL Lower Limit',
        defaultValue: true,
        order: 2,
        details: '(>=)'
      },
      male: {
        name: 'LDL_Male',
        displayName: 'Male',
        defaultValue: '160',
        order: 1
      },
      female: {
        name: 'LDL_Female',
        displayName: 'Female',
        defaultValue: '160',
        order: 2
      }
    },
    {
      checkbox: {
        name: 'TC_Checkbox',
        displayName: 'Total Cholesterol Lower Limit',
        defaultValue: true,
        order: 3,
        details: '(>=)'
      },
      male: {
        name: 'TC_Male',
        displayName: 'Male',
        defaultValue: '240',
        order: 1
      },
      female: {
        name: 'TC_Female',
        displayName: 'Female',
        defaultValue: '240',
        order: 2
      }
    },
    {
      checkbox: {
        name: 'TT_Checkbox',
        displayName: 'Total Triglycerides Lower Limit',
        defaultValue: true,
        order: 4,
        details: '(>=)'
      },
      male: {
        name: 'TT_Male',
        displayName: 'Male',
        defaultValue: '150',
        order: 1
      },
      female: {
        name: 'TT_Female',
        displayName: 'Female',
        defaultValue: '150',
        order: 2
      }
    }
  ];

  public formGroup: FormGroup = this.getDefaultFormGroup();

  constructor(
    private sharedService: SharedService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {

        // child diagnosis radio button group
        if (this.indication.selections.some(i => i.controlName === 'diagnosis')) {
          this.childDiagnosisComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'diagnosis').selectionValue);
        }

        // checkboxes
        if (this.indication.selections.some(i => i.controlName === 'HDL_Checkbox')){
          this.formGroup.controls.HDL_Checkbox.setValue(this.indication.selections.find(i => i.controlName === 'HDL_Checkbox').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'LDL_Checkbox')){
          this.formGroup.controls.LDL_Checkbox.setValue(this.indication.selections.find(i => i.controlName === 'LDL_Checkbox').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TC_Checkbox')){
          this.formGroup.controls.TC_Checkbox.setValue(this.indication.selections.find(i => i.controlName === 'TC_Checkbox').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TT_Checkbox')){
          this.formGroup.controls.TT_Checkbox.setValue(this.indication.selections.find(i => i.controlName === 'TT_Checkbox').selectionValue);
        }

        // inputs
        if (this.indication.selections.some(i => i.controlName === 'HDL_Male')){
          this.formGroup.controls.HDL_Male.setValue(this.indication.selections.find(i => i.controlName === 'HDL_Male').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'LDL_Male')){
          this.formGroup.controls.LDL_Male.setValue(this.indication.selections.find(i => i.controlName === 'LDL_Male').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TC_Male')){
          this.formGroup.controls.TC_Male.setValue(this.indication.selections.find(i => i.controlName === 'TC_Male').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TT_Male')){
          this.formGroup.controls.TT_Male.setValue(this.indication.selections.find(i => i.controlName === 'TT_Male').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'HDL_Female')){
          this.formGroup.controls.HDL_Female.setValue(this.indication.selections.find(i => i.controlName === 'HDL_Female').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'LDL_Female')){
          this.formGroup.controls.LDL_Female.setValue(this.indication.selections.find(i => i.controlName === 'LDL_Female').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TC_Female')){
          this.formGroup.controls.TC_Female.setValue(this.indication.selections.find(i => i.controlName === 'TC_Female').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === 'TT_Female')){
          this.formGroup.controls.TT_Female.setValue(this.indication.selections.find(i => i.controlName === 'TT_Female').selectionValue);
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childDiagnosisComponent.overrideInputValue('DU');
      this.numberOfSelectionsValue = 1;
      this.formGroup = this.getDefaultFormGroup();
      this.updateSelections();
    });
  }

  getDefaultFormGroup(): FormGroup {
    let form = {};
    for(let i = 0; i < this.dyslipidemiaCriteriaOptions.length; i++) {
      form[this.dyslipidemiaCriteriaOptions[i].checkbox.name] = new FormControl(this.dyslipidemiaCriteriaOptions[i].checkbox.defaultValue, []);
      form[this.dyslipidemiaCriteriaOptions[i].male.name] = new FormControl(this.dyslipidemiaCriteriaOptions[i].male.defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]);
      form[this.dyslipidemiaCriteriaOptions[i].female.name] = new FormControl(this.dyslipidemiaCriteriaOptions[i].female.defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]);
    }
    return this.formBuilder.group(form);
  }

  inputEnabled(inputOption: DyslipidemiaInputOption): boolean {
    const enabled = this.indication.selections.some(i => i.controlName === inputOption.checkbox.name && i.selectionValue === true);
    return enabled;
  }

  getNumberOfSelectionsCheckedList(): number[] {
    const checkedBoxesCount = this.indication.selections.filter(i => i.controlType === ControlTypeEnum.CHECKBOX && i.selectionValue === true).length;
    let numbersAvailable = [];
    for (let i = 1; i <= checkedBoxesCount; i++) {
      numbersAvailable.push(i);
    }
    return numbersAvailable;
  }

  childSharedControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childDiagnosisSelectionModel = selectionModel;
    if (!this.showCriteriaSection()) {
      this.formGroup = this.getDefaultFormGroup();
    }
    this.updateSelections();
  }
  
  showCriteriaSection(): boolean {
    const visible = this.childDiagnosisSelectionModel && 
    !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(this.childDiagnosisSelectionModel.selectionValue);
    return visible;
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];
    if (this.childDiagnosisSelectionModel) {
      currentSelections.push(this.childDiagnosisSelectionModel);
    }

    let currentMaxNumberOfSelectionsAvailable = [];
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      const type = key.split('_')[1];
      if (type === "Checkbox" && control.value === true) {
        currentMaxNumberOfSelectionsAvailable.push(control);
      }
    });

    if (currentMaxNumberOfSelectionsAvailable.length === 0) {
      this.hasErrors = true;
    } else {
      this.hasErrors = false;
    }

    if (this.numberOfSelectionsValue > currentMaxNumberOfSelectionsAvailable.length || this.numberOfSelectionsValue === 0) {
      this.numberOfSelectionsValue = currentMaxNumberOfSelectionsAvailable.length;
    }
    
    const criteriaSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'criteria',
      controlType: ControlTypeEnum.DROPDOWN,
      controlDisplayName: "Criteria",
      order: 1,
      selectionValue: this.numberOfSelectionsValue,
      selectionDisplayName: `Must satisfy ${this.numberOfSelectionsValue} of the selected following:`
    };
    currentSelections.push(criteriaSelection);
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      const type = key.split('_')[1];
      if (type === 'Checkbox') {
        const option = this.dyslipidemiaCriteriaOptions.find(i => i.checkbox.name === key);
        const currentSelection: SelectionsModel = {
          indicationName: this.indication.name,
          controlName: key,
          controlType: ControlTypeEnum.CHECKBOX,
          controlDisplayName: option.checkbox.displayName,
          order: option.checkbox.order,
          selectionValue: control.value,
          selectionDisplayName: option.checkbox.displayName 
        };
        currentSelections.push(currentSelection);
      } else {
        if (type === 'Male') {
          const option = this.dyslipidemiaCriteriaOptions.find(i => i.male.name === key);
          const currentSelection: SelectionsModel = {
            indicationName: this.indication.name,
            controlName: key,
            controlType: ControlTypeEnum.TEXT,
            controlDisplayName: option.male.displayName,
            order: option.male.order,
            selectionValue: control.value,
            selectionDisplayName: option.male.displayName 
          };
          currentSelections.push(currentSelection);
        } else { // Female
          const option = this.dyslipidemiaCriteriaOptions.find(i => i.female.name === key);
          const currentSelection: SelectionsModel = {
            indicationName: this.indication.name,
            controlName: key,
            controlType: ControlTypeEnum.TEXT,
            controlDisplayName: option.female.displayName,
            order: option.female.order,
            selectionValue: control.value,
            selectionDisplayName: option.female.displayName 
          };
          currentSelections.push(currentSelection);
        }
      }
    });

    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
